/**
 * Filter, sort, search and group-by evaluation for the in-memory source (spec M1-B4 §5.3–§5.4,
 * §8.5–§8.9).
 *
 * On the server all four are SQL over the shadow columns and the compiler "never contains a
 * `switch (columnType)`" (§5.3). The same discipline holds here: every operator works on the
 * type's own `bucketsOf` output or on the `text` / `number` / `date` shadows that
 * `packages/shared` computes, so this module never learns a type name either.
 */
import {
  compareAttributeSpec,
  findColumnType,
  type CellSortKeys,
  type ColumnId,
  type CompareAttributeSpec,
  type FilterOperator,
} from '@elizmonday/shared';
import { getColumnUi } from '../columns/registry';
import { valueContext } from '../columns/context';
import type { CellRenderContext } from '../columns/types';
import { BLANK_FACET, type FilterGroup, type FilterRule, type SortRule } from '../table/settings';
import type { BoardColumn, ItemRow } from './types';

export interface EvalContext {
  readonly columns: ReadonlyMap<string, BoardColumn>;
  readonly render: CellRenderContext;
  readonly now: Date;
}

function shadows(column: BoardColumn, value: unknown, context: EvalContext) {
  const shared = findColumnType(column.type);
  if (!shared) return { text: null, number: null, date: null };
  return shared.toSortKeys(value, column.settings, valueContext(context.render));
}

function bucketKeys(column: BoardColumn, value: unknown, context: EvalContext): (string | number)[] {
  return getColumnUi(column.type)
    .bucketsOf(value, column.settings, context.render)
    .map((bucket) => bucket.key);
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : value === null || value === undefined ? [] : [value];
}

function asNumber(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number(String(value ?? '').trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function asDate(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(String(value ?? ''));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const MS_PER_DAY = 86_400_000;

/**
 * The half of a compound value a rule's `compare_attribute` names (`timeline`'s `start` /
 * `end`, `creation_log`'s `person` / `date`, a `status`'s label `text`), or `null` when the
 * rule names none. The halves are declared **once**, by the column type
 * (`ColumnTypeDefinition.compareAttributes`), and `apps/api`'s `filter-compiler.ts` compiles
 * its SQL from the same declaration — before round 5z both evaluators simply ignored the
 * attribute, so `ends before X` was answered as `starts before X`.
 */
function halfOf(column: BoardColumn, rule: FilterRule): CompareAttributeSpec | null {
  const shared = findColumnType(column.type);
  return shared ? compareAttributeSpec(shared, rule.compareAttribute) : null;
}

/**
 * `any_of` against one half: the compiler compares the half's **shadow**
 * (`date_value::date = ANY(…)`, `text_value = ANY(…)`), not the type's buckets, so this does
 * the same — a date to the day, text and numbers exactly.
 */
function halfMatchesValue(half: CompareAttributeSpec, keys: CellSortKeys, entry: unknown): boolean {
  if (half.shadow === 'date') {
    if (keys.date === null) return false;
    return keys.date.toISOString().slice(0, 10) === String(entry ?? '').slice(0, 10);
  }
  if (half.shadow === 'number') {
    const wanted = asNumber(entry);
    return keys.number !== null && wanted !== null && keys.number === wanted;
  }
  return keys.text !== null && keys.text === String(entry ?? '');
}

/**
 * One rule against one item. Unknown columns match nothing — the pane drops them on read (E1),
 * so reaching here means the filter is stale and the conservative answer is "no match".
 */
export function matchesRule(item: ItemRow, rule: FilterRule, context: EvalContext): boolean {
  const column = context.columns.get(rule.columnId);
  if (!column) return false;
  const value = column.id === 'name' ? item.name : (item.values[column.id] ?? null);
  const empty = value === null || value === undefined || value === '';
  const half = halfOf(column, rule);
  // The shadows the ordered, text and relative operators compare: the half's when the rule
  // names one, the whole value's otherwise.
  const shadow = (): CellSortKeys =>
    half ? half.keys(value, column.settings, valueContext(context.render)) : shadows(column, value, context);

  switch (rule.operator satisfies FilterOperator) {
    case 'is_empty':
      return empty;
    case 'is_not_empty':
      return !empty;
    case 'any_of': {
      const wanted = asArray(rule.compareValue);
      // monday's own "blank" sentinel is translated to `is_empty` before SQL `[B4§5.3]`.
      if (wanted.some((entry) => entry === BLANK_FACET) && empty) return true;
      if (half) {
        const keys = shadow();
        return wanted.some((entry) => halfMatchesValue(half, keys, entry));
      }
      const keys = bucketKeys(column, value, context).map(String);
      return wanted.some((entry) => keys.includes(String(entry)));
    }
    case 'not_any_of': {
      const wanted = asArray(rule.compareValue);
      if (wanted.some((entry) => entry === BLANK_FACET) && empty) return false;
      if (half) {
        const keys = shadow();
        return !wanted.some((entry) => halfMatchesValue(half, keys, entry));
      }
      const keys = bucketKeys(column, value, context).map(String);
      return !wanted.some((entry) => keys.includes(String(entry)));
    }
    case 'contains_text':
    case 'not_contains_text':
    case 'starts_with':
    case 'ends_with':
    case 'contains_terms': {
      const text = (shadow().text ?? '').toLowerCase();
      const needle = String(rule.compareValue ?? '').toLowerCase();
      if (rule.operator === 'starts_with') return text.startsWith(needle);
      if (rule.operator === 'ends_with') return text.endsWith(needle);
      if (rule.operator === 'contains_terms') {
        return needle
          .split(/\s+/)
          .filter(Boolean)
          .every((term) => text.includes(term));
      }
      const hit = needle !== '' && text.includes(needle);
      return rule.operator === 'contains_text' ? hit : !hit;
    }
    case 'greater_than':
    case 'greater_than_or_equals':
    case 'lower_than':
    case 'lower_than_or_equal':
    case 'between': {
      const keys = shadow();
      const left = keys.number ?? keys.date?.getTime() ?? null;
      if (left === null) return false;
      if (rule.operator === 'between') {
        const [from, to] = asArray(rule.compareValue);
        const min = asNumber(from) ?? asDate(from)?.getTime() ?? null;
        const max = asNumber(to) ?? asDate(to)?.getTime() ?? null;
        if (min === null || max === null) return false;
        return left >= min && left <= max;
      }
      const right =
        asNumber(rule.compareValue) ?? asDate(rule.compareValue)?.getTime() ?? null;
      if (right === null) return false;
      if (rule.operator === 'greater_than') return left > right;
      if (rule.operator === 'greater_than_or_equals') return left >= right;
      if (rule.operator === 'lower_than') return left < right;
      return left <= right;
    }
    case 'within_the_last':
    case 'within_the_next': {
      const date = shadow().date;
      if (!date) return false;
      const days = asNumber(rule.compareValue) ?? 0;
      const delta = date.getTime() - context.now.getTime();
      return rule.operator === 'within_the_last'
        ? delta <= 0 && delta >= -days * MS_PER_DAY
        : delta >= 0 && delta <= days * MS_PER_DAY;
    }
    default:
      return true;
  }
}

/** `compileFilter` of §5.3, evaluated instead of emitted. */
export function matchesFilter(item: ItemRow, group: FilterGroup | null, context: EvalContext): boolean {
  if (!group) return true;
  const parts: boolean[] = [
    ...group.rules.map((rule) => matchesRule(item, rule, context)),
    ...group.groups.map((child) => matchesFilter(item, child, context)),
  ];
  if (parts.length === 0) return true;
  return group.operator === 'or' ? parts.some(Boolean) : parts.every(Boolean);
}

/**
 * §8.9 board search: the item **name or any visible text cell** matches, case-insensitively.
 * The server does the same with `text_value ILIKE '%q%' OR items.name ILIKE '%q%'`.
 */
export function matchesSearch(
  item: ItemRow,
  term: string,
  visibleColumns: readonly BoardColumn[],
  context: EvalContext,
): boolean {
  const needle = term.trim().toLowerCase();
  if (needle === '') return true;
  if (item.name.toLowerCase().includes(needle)) return true;
  return visibleColumns.some((column) => {
    if (column.id === 'name') return false;
    const text = shadows(column, item.values[column.id] ?? null, context).text;
    return text !== null && text.toLowerCase().includes(needle);
  });
}

/**
 * §5.3: `ORDER BY <shadow of column 1> <dir>, …, items.id ASC`. The final `items.id` tiebreaker
 * is what makes cursor paging safe (F5).
 */
export function compareItems(
  a: ItemRow,
  b: ItemRow,
  sort: readonly SortRule[],
  context: EvalContext,
): number {
  for (const rule of sort) {
    const column = context.columns.get(rule.columnId);
    if (!column) continue;
    const left = sortKey(a, column, context);
    const right = sortKey(b, column, context);
    const order = compareKeys(left, right);
    if (order !== 0) return rule.direction === 'desc' ? -order : order;
  }
  // Unsorted (and every tie) falls back to position, then the id `[B3§7]`.
  if (a.position !== b.position) return a.position - b.position;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function sortKey(item: ItemRow, column: BoardColumn, context: EvalContext): number | string | null {
  const value = column.id === 'name' ? item.name : (item.values[column.id] ?? null);
  const shadow = shadows(column, value, context);
  if (shadow.number !== null) return shadow.number;
  if (shadow.date !== null) return shadow.date.getTime();
  return shadow.text;
}

/**
 * An empty cell is the **largest** key, and the direction reverses it with everything else: it
 * sorts last ascending and **first** descending. Measured, not assumed — `status` ascending on
 * the sandbox board puts the four empty rows at the bottom of each group and descending puts
 * them at the top (P-292). The comment here used to claim "last, whichever direction", which the
 * code never did and monday never does.
 */
function compareKeys(a: number | string | null, b: number | string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/* ── group by (§8.8) ──────────────────────────────────────────────────────── */

export interface VirtualBucket {
  readonly key: string;
  readonly label: string;
  readonly color: string | null;
}

/**
 * The bucket an item with no value falls into.
 *
 * It is **monday's** `$$$blank$$$` / `Blank`, the same literal `BLANK_FACET` already carries
 * here and `services/board-read.ts` serves on the real api (`[B6§4.2]`, `[B6-5]`). It used to
 * be a local `__blank__` / *Unassigned*, which meant the in-memory source and the server
 * disagreed about the id of the group-by bucket every view addresses — `views/kanban/lanes.ts`
 * had to accept both so a drop on the blank lane cleared the cell on either source (defect 2
 * on `tasks/T-M4-V2-V4-V7.md`). One spelling now, and it is monday's.
 */
export const BLANK_BUCKET: VirtualBucket = { key: BLANK_FACET, label: 'Blank', color: null };

/** The bucket an item falls into for a group-by column. The **first** bucket wins (Q171). */
export function bucketOf(item: ItemRow, column: BoardColumn, context: EvalContext): VirtualBucket {
  const value = column.id === 'name' ? item.name : (item.values[column.id] ?? null);
  const buckets = getColumnUi(column.type).bucketsOf(value, column.settings, context.render);
  const first = buckets[0];
  if (!first) return BLANK_BUCKET;
  return { key: String(first.key), label: first.label, color: first.color ?? null };
}

/**
 * Order a group-by column's buckets the way `services/board-read.ts` does: the type's
 * **declared vocabulary** first (a status by `labels_positions_v2`, a dropdown by option
 * order), then whatever else appeared, in first-appearance order, and `Blank` **last** —
 * `desc` reverses the first two and leaves `Blank` where it is (`[B6§4.7]`, `[B6-5]`).
 * Sorting the keys as strings, which is what the in-memory source used to do, put `Blank`
 * wherever its sentinel happened to collate.
 */
export function orderBucketKeys(
  column: BoardColumn,
  keys: readonly string[],
  direction: 'asc' | 'desc',
): string[] {
  const present = new Set(keys);
  const declared = (findColumnType(column.type)?.bucketVocabulary?.(column.settings) ?? []).map((bucket) =>
    String(bucket.key),
  );
  const ordered: string[] = [];
  for (const key of declared) if (present.has(key) && key !== BLANK_FACET) ordered.push(key);
  for (const key of keys) {
    if (key === BLANK_FACET || declared.includes(key) || ordered.includes(key)) continue;
    ordered.push(key);
  }
  if (direction === 'desc') ordered.reverse();
  if (present.has(BLANK_FACET)) ordered.push(BLANK_FACET);
  return ordered;
}

/** Every bucket the column *could* produce, for `Show empty groups`. */
export function allBuckets(column: BoardColumn, items: readonly ItemRow[], context: EvalContext): VirtualBucket[] {
  const seen = new Map<string, VirtualBucket>();
  for (const item of items) {
    const value = column.id === 'name' ? item.name : (item.values[column.id] ?? null);
    for (const bucket of getColumnUi(column.type).bucketsOf(value, column.settings, context.render)) {
      const key = String(bucket.key);
      if (!seen.has(key)) seen.set(key, { key, label: bucket.label, color: bucket.color ?? null });
    }
  }
  return [...seen.values()];
}

/** The virtual group id `[B4§5.1]`: `gb:<columnId>:<bucketKey>`. */
export function virtualGroupId(columnId: ColumnId, bucketKey: string): string {
  return `gb:${columnId}:${bucketKey}`;
}

export function parseVirtualGroupId(id: string): { columnId: string; bucketKey: string } | null {
  if (!id.startsWith('gb:')) return null;
  const rest = id.slice(3);
  const at = rest.indexOf(':');
  if (at < 0) return null;
  return { columnId: rest.slice(0, at), bucketKey: rest.slice(at + 1) };
}
